import type { GetTextOptions } from "vue3-gettext";

import { getRuntimeConfig } from "../utils/runtimeData";
import type { RequiredNotNullOrUndefined } from "./../utils/typeHelpers";

// Static values in:
// src/core/config/env.d.ts

type RuntimeConfig = ImportMetaEnv;

const dynamicRuntimeConfig = getRuntimeConfig();

// --- Base Config ---

const defaultBaseConfig: RequiredNotNullOrUndefined<
  Pick<RuntimeConfig, "BASE_URL" | "NODE_ENV" | "VITE_APP_MODE" | "VITE_APP_PUBLIC_INSTANCE_ORIGIN">
> = {
  BASE_URL: "/",
  NODE_ENV: "development",
  VITE_APP_MODE: "public",
  VITE_APP_PUBLIC_INSTANCE_ORIGIN: "",
};

const rawBaseConfig = {
  BASE_URL: dynamicRuntimeConfig.BASE_URL
    ? dynamicRuntimeConfig.BASE_URL
    : import.meta.env.BASE_URL
    ? import.meta.env.BASE_URL
    : defaultBaseConfig.BASE_URL,
  NODE_ENV: dynamicRuntimeConfig.NODE_ENV
    ? dynamicRuntimeConfig.NODE_ENV
    : import.meta.env.NODE_ENV
    ? import.meta.env.NODE_ENV
    : defaultBaseConfig.NODE_ENV,
  VITE_APP_MODE: dynamicRuntimeConfig.VITE_APP_MODE
    ? dynamicRuntimeConfig.VITE_APP_MODE
    : import.meta.env.VITE_APP_MODE
    ? import.meta.env.VITE_APP_MODE
    : defaultBaseConfig.VITE_APP_MODE,
  VITE_APP_PUBLIC_INSTANCE_ORIGIN: dynamicRuntimeConfig.VITE_APP_PUBLIC_INSTANCE_ORIGIN
    ? dynamicRuntimeConfig.VITE_APP_PUBLIC_INSTANCE_ORIGIN
    : import.meta.env.VITE_APP_PUBLIC_INSTANCE_ORIGIN
    ? import.meta.env.VITE_APP_PUBLIC_INSTANCE_ORIGIN
    : defaultBaseConfig.VITE_APP_PUBLIC_INSTANCE_ORIGIN,
};

const baseConfig = rawBaseConfig;

// --- Auth Config ---

const defaultAuthConfig: RequiredNotNullOrUndefined<
  Pick<RuntimeConfig, "VITE_APP_OIDC_CLIENT_ID" | "VITE_APP_OIDC_ROLE_NAMESPACE" | "VITE_APP_OIDC_AUTHORITY">
> = {
  VITE_APP_OIDC_CLIENT_ID: "", // TODO: Add default
  VITE_APP_OIDC_AUTHORITY: "", // TODO: Add default
  VITE_APP_OIDC_ROLE_NAMESPACE: "", // TODO: Add default
};

const rawAuthConfig = {
  VITE_APP_OIDC_CLIENT_ID: dynamicRuntimeConfig.VITE_APP_OIDC_CLIENT_ID
    ? dynamicRuntimeConfig.VITE_APP_OIDC_CLIENT_ID
    : import.meta.env.VITE_APP_OIDC_CLIENT_ID
    ? import.meta.env.VITE_APP_OIDC_CLIENT_ID
    : defaultAuthConfig.VITE_APP_OIDC_CLIENT_ID,
  VITE_APP_OIDC_AUTHORITY: dynamicRuntimeConfig.VITE_APP_OIDC_AUTHORITY
    ? dynamicRuntimeConfig.VITE_APP_OIDC_AUTHORITY
    : import.meta.env.VITE_APP_OIDC_AUTHORITY
    ? import.meta.env.VITE_APP_OIDC_AUTHORITY
    : defaultAuthConfig.VITE_APP_OIDC_AUTHORITY,
  VITE_APP_OIDC_ROLE_NAMESPACE: dynamicRuntimeConfig.VITE_APP_OIDC_ROLE_NAMESPACE
    ? dynamicRuntimeConfig.VITE_APP_OIDC_ROLE_NAMESPACE
    : import.meta.env.VITE_APP_OIDC_ROLE_NAMESPACE
    ? import.meta.env.VITE_APP_OIDC_ROLE_NAMESPACE
    : defaultAuthConfig.VITE_APP_OIDC_ROLE_NAMESPACE,
};

const authConfig = rawAuthConfig;

// --- API Config ---

const defaultApiConfig: RequiredNotNullOrUndefined<
  Pick<
    RuntimeConfig,
    "VITE_APP_APIS_BACKEND_API_URL" | "VITE_APP_APIS_BACKEND_WS_URL" | "VITE_APP_APIS_MASTERDATA_API_URL"
  >
> = {
  VITE_APP_APIS_BACKEND_API_URL: "https://rental-deposit-api-integration.acrevison.ch",
  VITE_APP_APIS_BACKEND_WS_URL: "wss://rental-deposit-api-integration.acrevison.ch",
  VITE_APP_APIS_MASTERDATA_API_URL: "https://masterdata-core.acrevison.ch",
};

const rawApiConfig = {
  backend: {
    api: dynamicRuntimeConfig.VITE_APP_APIS_BACKEND_API_URL
      ? dynamicRuntimeConfig.VITE_APP_APIS_BACKEND_API_URL
      : import.meta.env.VITE_APP_APIS_BACKEND_API_URL
      ? import.meta.env.VITE_APP_APIS_BACKEND_API_URL
      : defaultApiConfig.VITE_APP_APIS_BACKEND_API_URL,
    ws: dynamicRuntimeConfig.VITE_APP_APIS_BACKEND_WS_URL
      ? dynamicRuntimeConfig.VITE_APP_APIS_BACKEND_WS_URL
      : import.meta.env.VITE_APP_APIS_BACKEND_WS_URL
      ? import.meta.env.VITE_APP_APIS_BACKEND_WS_URL
      : defaultApiConfig.VITE_APP_APIS_BACKEND_WS_URL,
  },
  masterdata: {
    api: dynamicRuntimeConfig.VITE_APP_APIS_MASTERDATA_API_URL
      ? dynamicRuntimeConfig.VITE_APP_APIS_MASTERDATA_API_URL
      : import.meta.env.VITE_APP_APIS_MASTERDATA_API_URL
      ? import.meta.env.VITE_APP_APIS_MASTERDATA_API_URL
      : defaultApiConfig.VITE_APP_APIS_MASTERDATA_API_URL,
  },
};

const apiConfig = rawApiConfig;

// --- I18N Config ---

const defaultI18nConfig: RequiredNotNullOrUndefined<Pick<RuntimeConfig, "VITE_APP_I18N_DEFAULT_LANGUAGE">> = {
  VITE_APP_I18N_DEFAULT_LANGUAGE: "de_CH",
};

const rawI18nConfig: Pick<GetTextOptions, "defaultLanguage" | "availableLanguages"> = {
  defaultLanguage: dynamicRuntimeConfig.VITE_APP_I18N_DEFAULT_LANGUAGE
    ? dynamicRuntimeConfig.VITE_APP_I18N_DEFAULT_LANGUAGE
    : import.meta.env.VITE_APP_I18N_DEFAULT_LANGUAGE
    ? import.meta.env.VITE_APP_I18N_DEFAULT_LANGUAGE
    : defaultI18nConfig.VITE_APP_I18N_DEFAULT_LANGUAGE,
  availableLanguages: {
    en_US: "English (United States)",
    de_CH: "German (Switzerland)",
  },
};

const i18nConfig = rawI18nConfig;

// --- Sentry Config ---

const defaultSentryConfig: Pick<
  RuntimeConfig,
  "VITE_APP_SENTRY_ENABLED" | "VITE_APP_SENTRY_DSN" | "VITE_APP_SENTRY_ENVIRONMENT" | "VITE_APP_SENTRY_RELEASE"
> = {
  VITE_APP_SENTRY_ENABLED: "false",
  VITE_APP_SENTRY_DSN: undefined,
  VITE_APP_SENTRY_ENVIRONMENT: undefined,
  VITE_APP_SENTRY_RELEASE: undefined,
};

const rawSentryConfig = {
  enabled: dynamicRuntimeConfig.VITE_APP_SENTRY_ENABLED
    ? dynamicRuntimeConfig.VITE_APP_SENTRY_ENABLED === "true"
    : import.meta.env.VITE_APP_SENTRY_ENABLED
    ? import.meta.env.VITE_APP_SENTRY_ENABLED === "true"
    : defaultSentryConfig.VITE_APP_SENTRY_ENABLED === "true",
  dsn: dynamicRuntimeConfig.VITE_APP_SENTRY_DSN
    ? dynamicRuntimeConfig.VITE_APP_SENTRY_DSN
    : import.meta.env.VITE_APP_SENTRY_DSN
    ? import.meta.env.VITE_APP_SENTRY_DSN
    : defaultSentryConfig.VITE_APP_SENTRY_DSN,
  environment: dynamicRuntimeConfig.VITE_APP_SENTRY_ENVIRONMENT
    ? dynamicRuntimeConfig.VITE_APP_SENTRY_ENVIRONMENT
    : import.meta.env.VITE_APP_SENTRY_ENVIRONMENT
    ? import.meta.env.VITE_APP_SENTRY_ENVIRONMENT
    : defaultSentryConfig.VITE_APP_SENTRY_ENVIRONMENT,
  release: dynamicRuntimeConfig.VITE_APP_SENTRY_RELEASE
    ? dynamicRuntimeConfig.VITE_APP_SENTRY_RELEASE
    : import.meta.env.VITE_APP_SENTRY_RELEASE
    ? import.meta.env.VITE_APP_SENTRY_RELEASE
    : defaultSentryConfig.VITE_APP_SENTRY_RELEASE,
  transactionId: Math.random().toString(36).substring(2, 9),
  silent: false,
};

const sentryConfig = {
  enabled: rawSentryConfig.environment,
  dsn: rawSentryConfig.dsn,
  transactionId: Math.random().toString(36).substring(2, 9),
  release: rawSentryConfig.release,
  environment: rawSentryConfig.environment,
  silent: false,
  shouldTrack:
    rawSentryConfig.enabled &&
    rawSentryConfig.dsn !== undefined &&
    rawSentryConfig.environment !== undefined &&
    rawSentryConfig.release !== undefined,
};

const rawDevConfig = {
  testDataEnabled: dynamicRuntimeConfig.VITE_APP_ENABLE_TEST_DATA
    ? dynamicRuntimeConfig.VITE_APP_ENABLE_TEST_DATA === "true"
    : import.meta.env.VITE_APP_ENABLE_TEST_DATA
    ? import.meta.env.VITE_APP_ENABLE_TEST_DATA === "true"
    : false,
};

const devConfig = {
  testDataEnabled: rawDevConfig.testDataEnabled,
};

export { apiConfig, authConfig, baseConfig, devConfig, i18nConfig, sentryConfig };
export type { RuntimeConfig };
